import React from 'react';
import { useStatsigClient } from '@statsig/react-bindings';
import { useBusinessMetrics } from '../hooks/useStatsigFeatures';
import { trackEventServerSide, trackFeatureUsageServerSide } from '../utils/serverSideAnalytics';
import PageHeader from '../components/PageHeader';
import { StatsigExample } from '../components/StatsigExample';
import { StatsigPrivacyDemo } from '../components/privacy/StatsigPrivacyDemo';

const StatsigDemo: React.FC = () => {
  const statsigClient = useStatsigClient();
  const { trackFeatureUsage, trackUserJourney } = useBusinessMetrics();

  // Override parent overflow constraints for this page
  React.useEffect(() => {
    const mainElement = document.querySelector('main');
    if (mainElement) {
      const originalOverflow = mainElement.style.overflow;
      mainElement.style.overflow = 'auto';
      
      return () => {
        mainElement.style.overflow = originalOverflow;
      };
    }
  }, []);

  const handleTestEvent = () => {
    // Test direct client analytics
    trackEventServerSide('test_event_from_demo', {
      timestamp: Date.now().toString(),
      source: 'statsig_demo_page',
      test_type: 'direct_client_analytics'
    });
    
    // Test business metrics tracking
    trackFeatureUsage('statsig_demo_test', 'button_click');
    trackUserJourney('demo_page_test_clicked', {
      button_type: 'test_event',
      page: 'statsig_demo'
    });
    
    alert('Direct Statsig analytics events sent! Check your Statsig dashboard.');
  };

  const handleTestDirectStatsig = () => {
    // Test direct client logging
    if (statsigClient) {
      statsigClient.logEvent('test_direct_statsig', undefined, {
        timestamp: Date.now().toString(),
        source: 'statsig_demo_page',
        test_type: 'direct_client'
      });
      
      alert('Direct Statsig event sent!');
    } else {
      alert('Statsig client not available. Check console for errors.');
    }
  };

  const handleTestServerSideFeature = () => {
    // Test feature usage tracking
    trackFeatureUsageServerSide('demo_feature_test', 'button_click', {
      feature_category: 'testing',
      user_action: 'manual_test',
      page: 'statsig_demo'
    });
    
    alert('Feature usage tracked!');
  };

  const checkStatsigStatus = () => {
    console.group('🔍 Statsig Debug Information');
    console.log('Client Key:', import.meta.env.VITE_STATSIG_CLIENT_KEY ? 
      import.meta.env.VITE_STATSIG_CLIENT_KEY.substring(0, 10) + '...' : 'NOT SET');
    console.log('Environment:', import.meta.env.MODE);
    console.log('Statsig Client Available:', !!statsigClient);
    console.log('Client Object:', statsigClient);
    console.groupEnd();
  };

  React.useEffect(() => {
    checkStatsigStatus();
  }, [statsigClient]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <PageHeader 
        title="Statsig Integration Demo" 
        subtitle="Test feature flags, experiments, and analytics"
      />
      
      <div className="max-w-4xl mx-auto p-6 space-y-8">
        {/* Direct Statsig Analytics Testing */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
            📊 Direct Statsig Analytics
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            These events go directly to Statsig using the client SDK.
          </p>
          <div className="space-y-3">
            <button
              onClick={handleTestEvent}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md mr-3"
            >
              Test Analytics Event
            </button>
            <button
              onClick={handleTestServerSideFeature}
              className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600"
            >
              Test Feature Usage
            </button>
            <button
              onClick={handleTestDirectStatsig}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
            >
              Test Direct Statsig
            </button>
          </div>
        </div>

        {/* Debug Information */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
            🔍 Debug Information
          </h2>
          <button
            onClick={checkStatsigStatus}
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md mr-3"
          >
            Check Console
          </button>
        </div>

        {/* Feature Flags Demo */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
            🚩 Feature Flags & Experiments
          </h2>
          <StatsigExample />
        </div>

        {/* Privacy Controls */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
            🔐 Privacy Controls
          </h2>
          <StatsigPrivacyDemo />
        </div>
      </div>
    </div>
  );
};

export default StatsigDemo; 